import { useEffect } from 'react'
import websocket from '@/utils/websocket'
import { useCrmIntegrationChatAvailability } from '@/hooks/useCrmIntegrationChatAvailability'
import { useCrmIntegrationChatUserAccess } from '@/hooks/useCrmIntegrationChatUserAccess'
import {
  isIntegrationChannelDeletedWsMessage,
  isIntegrationMessageDeletedWsMessage,
  isIntegrationMessageInsertedWsMessage,
  isIntegrationMessageReadWsMessage,
  isVkMessageInsertedWsMessage,
  isVkMessageReadWsMessage,
} from '@pages/Admin/CRM/utils/crmRealtimeWebSocket'
import { hasAnyCrmIntegrationChatConnected } from '@pages/Admin/CRM/utils/crmIntegrationChatAvailability'
import { useIntegrationChatDockBrowserTabVisibleRefetch } from './useIntegrationChatDockBrowserTabVisibleRefetch'
import { useIntegrationChatDockGlobalUnreadRefetch } from './useIntegrationChatDockGlobalUnreadRefetch'

const IntegrationChatDockLauncherRealtimeBridge = () => {
  useIntegrationChatDockBrowserTabVisibleRefetch()
  const { isLoading, providers } = useCrmIntegrationChatAvailability()
  const hasUserAccess = useCrmIntegrationChatUserAccess()
  const scheduleGlobalUnreadRefetchRef =
    useIntegrationChatDockGlobalUnreadRefetch()

  const canSyncGlobalUnread =
    !isLoading &&
    hasAnyCrmIntegrationChatConnected(providers) &&
    hasUserAccess

  useEffect(() => {
    if (!canSyncGlobalUnread) {
      return
    }

    websocket.connect()

    const onVkWs = (message: unknown) => {
      if (
        !isVkMessageInsertedWsMessage(message) &&
        !isVkMessageReadWsMessage(message) &&
        !isIntegrationMessageInsertedWsMessage(message) &&
        !isIntegrationMessageReadWsMessage(message) &&
        !isIntegrationMessageDeletedWsMessage(message) &&
        !isIntegrationChannelDeletedWsMessage(message)
      ) {
        return
      }

      scheduleGlobalUnreadRefetchRef.current()
    }

    const unsubscribeInserted = websocket.subscribe(
      'vk_message_inserted',
      onVkWs,
    )
    const unsubscribeRead = websocket.subscribe('vk_message_read', onVkWs)
    const unsubscribeIntegration = websocket.subscribe(
      'integration_message_inserted',
      onVkWs,
    )
    const unsubscribeIntegrationRead = websocket.subscribe(
      'integration_message_read',
      onVkWs,
    )
    const unsubscribeIntegrationDeleted = websocket.subscribe(
      'integration_message_deleted',
      onVkWs,
    )
    const unsubscribeChannelDeleted = websocket.subscribe(
      'integration_channel_deleted',
      onVkWs,
    )

    return () => {
      unsubscribeInserted?.()
      unsubscribeRead?.()
      unsubscribeIntegration?.()
      unsubscribeIntegrationRead?.()
      unsubscribeIntegrationDeleted?.()
      unsubscribeChannelDeleted?.()
    }
  }, [canSyncGlobalUnread, scheduleGlobalUnreadRefetchRef])

  return null
}

export default IntegrationChatDockLauncherRealtimeBridge
